import { useCallback, useState } from "react";
import { play } from "../lib/sound";
import { Button, Card } from "./ui";

/**
 * The check before the app hands anything to another app.
 *
 * Apple requires one in front of every way out of a Kids Category app — the
 * share sheet, a link to a website, anything that costs money. This app only
 * has one such exit, the invite share sheet, and it exists so a grown-up can
 * send a room code to another grown-up.
 *
 * A sum, with the numbers written as words. Written as words on purpose: the
 * digits are the easy part, and a child who is nine can multiply. Reading
 * "seventeen times six" and holding both halves while working it out is the
 * bit that is meant to be tedious, and the answer is typed rather than picked
 * from a list, so there is nothing to guess at.
 *
 * The keypad is the one place in the whole app a number is typed, and it never
 * leaves the device.
 */

const ONES = ["zero", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine"];
const TEENS = [
	"ten",
	"eleven",
	"twelve",
	"thirteen",
	"fourteen",
	"fifteen",
	"sixteen",
	"seventeen",
	"eighteen",
	"nineteen",
];

function inWords(value: number): string {
	return value < 10 ? ONES[value] : TEENS[value - 10];
}

interface Sum {
	left: number;
	right: number;
	answer: number;
}

/** Deliberately awkward: a teen times a single digit, never one or two. */
export function newSum(pick: () => number = Math.random): Sum {
	const left = 12 + Math.floor(pick() * 8);
	const right = 3 + Math.floor(pick() * 7);
	return { left, right, answer: left * right };
}

interface Props {
	/** What the grown-up is being asked to allow, in their words. */
	action: string;
	onPass: () => void;
	onCancel: () => void;
}

export function ParentalGate({ action, onPass, onCancel }: Props) {
	const [sum, setSum] = useState(newSum);
	const [typed, setTyped] = useState("");
	const [wrong, setWrong] = useState(false);

	const submit = useCallback(() => {
		if (Number(typed) === sum.answer) {
			play("win");
			onPass();
			return;
		}
		// A fresh sum, so a wrong answer cannot be worn down by trying again.
		play("bad");
		setWrong(true);
		setSum(newSum());
		setTyped("");
	}, [typed, sum.answer, onPass]);

	return (
		<div
			className="fixed inset-0 z-50 flex items-center justify-center bg-indigo-950/60 p-4 backdrop-blur-sm"
			role="dialog"
			aria-modal="true"
			aria-label="Ask a grown-up"
		>
			<Card className="w-full max-w-sm text-center">
				<p className="text-2xl font-black">Ask a grown-up 🧑‍🦰</p>
				<p className="mt-1 text-indigo-900/70 dark:text-indigo-100/80">{action}</p>

				<p className="mt-5 text-xl font-bold">
					What is <span className="whitespace-nowrap">{inWords(sum.left)}</span> times{" "}
					<span className="whitespace-nowrap">{inWords(sum.right)}</span>?
				</p>

				<input
					// The only number typed anywhere in the app, and it stays here.
					value={typed}
					onChange={(event) => setTyped(event.target.value.replace(/\D/g, "").slice(0, 4))}
					onKeyDown={(event) => {
						if (event.key === "Enter") submit();
					}}
					inputMode="numeric"
					autoComplete="off"
					// biome-ignore lint/a11y/noAutofocus: a modal whose only field is this
					autoFocus
					aria-label="The answer"
					aria-invalid={wrong}
					className="mt-4 w-32 rounded-2xl bg-white/85 py-3 text-center text-3xl font-black shadow-inner outline-none focus:ring-4 focus:ring-indigo-400 dark:bg-black/25"
				/>

				<p
					role="status"
					className="mt-2 h-5 text-sm font-semibold text-rose-700 dark:text-rose-300"
				>
					{wrong ? "Not quite — here's another one." : ""}
				</p>

				<div className="mt-4 flex justify-center gap-3">
					<Button onClick={submit} disabled={typed.length === 0}>
						Continue
					</Button>
					<Button tone="secondary" onClick={onCancel}>
						Cancel
					</Button>
				</div>
			</Card>
		</div>
	);
}
